import React, {useState, useCallback, useEffect, useRef, useMemo} from 'react';
import PropTypes from 'prop-types';
import ErrorContext from './ErrorContextUtils';
import {setGlobalErrorHandler} from '@/helpers/apiClient';

/**
 * Provides error context to children components.
 * Handles global error reporting and error visibility state.
 */
export const ErrorProvider = ({children}) => {
  const [error, setError] = useState(null);
  const [isErrorVisible, setIsErrorVisible] = useState(false);

  /**
   * Show an error message and display the error UI.
   * @param {string} message
   */
  const showError = useCallback((message) => {
    setError(message);
    setIsErrorVisible(true);
  }, []);

  /**
   * Hide the error UI and clear the error message.
   */
  const hideError = useCallback(() => {
    setIsErrorVisible(false);
    setError(null);
  }, []);

  // Use a ref to always have the latest showError
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  // Set the global error handler on mount
  useEffect(() => {
    setGlobalErrorHandler((msg) => {
      showErrorRef.current(msg);
    });
  }, []);

  const contextValue = useMemo(
    () => ({error, isErrorVisible, showError, hideError}),
    [error, isErrorVisible, showError, hideError]
  );

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

ErrorProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Custom hook to consume the error context.
 * @returns {{ error: string|null, isErrorVisible: boolean, showError: function, hideError: function }}
 */
// useErrorContext has been moved to useErrorContext.js for proper fast refresh support.
